/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI.
 * 
 * Requirements: 13.5
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';

/**
 * Props for the ErrorBoundary component
 */
interface ErrorBoundaryProps {
    /** Child components to render when no error is present */
    children: ReactNode;
}

/**
 * Internal state for the ErrorBoundary component
 */
interface ErrorBoundaryState {
    /** Whether an error has been caught in the component tree */
    hasError: boolean;

    /** The caught error object, null when no error */
    error: Error | null;
}

/**
 * ErrorBoundary Component
 * 
 * React error boundary that catches errors in child components
 * and displays a user-friendly error message with recovery options.
 * 
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log error details for debugging
        console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    handleReset = (): void => {
        // Reset error state and reload the page
        this.setState({
            hasError: false,
            error: null,
        });
        window.location.reload();
    };

    render(): ReactNode {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        backgroundColor: '#f5f5f5',
                        padding: 3,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            padding: 4,
                            maxWidth: 600,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h4" color="error" gutterBottom>
                            Something went wrong
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            An unexpected error occurred. Please try refreshing the page.
                        </Typography>
                        {this.state.error && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontFamily: 'monospace',
                                    backgroundColor: '#f5f5f5',
                                    padding: 2,
                                    borderRadius: 1,
                                    marginBottom: 2,
                                    textAlign: 'left',
                                    overflow: 'auto',
                                }}
                            >
                                {this.state.error.message}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={this.handleReset}
                            sx={{ marginTop: 2 }}
                        >
                            Reload Page
                        </Button>
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}
